import { React, useRef, useState } from 'react';

import { Box, TextField } from '@mui/material';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import ContactPhoneRoundedIcon from '@mui/icons-material/ContactPhoneRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import PixRoundedIcon from '@mui/icons-material/PixRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import RequestQuoteRoundedIcon from '@mui/icons-material/RequestQuoteRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';

import { Alerta } from '../../components/alerta';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import { fetchFlask } from '../../utils/fetchFlask';
import { maskPhone } from '../../utils/maskCpfCnpj';
import { normalizeNumericText, parseNumericText } from '../../utils/numberUtils';
import { formatCurrency, getDigits } from '../../utils/stringFormat';
import '../css/refundRequest.css';

const getCurrencyPreview = (value) => {
    const parsedValue = parseNumericText(value, null);

    return parsedValue === null ? 'Não informado' : formatCurrency(parsedValue);
};

const SolicitacaoReembolso = () => {
    const fileInputRef = useRef(null);

    const [cc, setCc] = useState('');
    const [nf, setNf] = useState('');
    const [valor, setValor] = useState('');
    const [chavePix, setChavePix] = useState('');
    const [protocoloAtendimento, setProtocoloAtendimento] = useState('');
    const [telefone, setTelefone] = useState('');
    const [motivo, setMotivo] = useState('');
    const [comprovante, setComprovante] = useState(null);
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);

    const handleAlerta = (texto, tipo) => {
        setMessage(texto);
        setStatus(tipo);
        setOpenAlerta(true);
    };

    const clearForm = () => {
        setCc('');
        setNf('');
        setValor('');
        setChavePix('');
        setProtocoloAtendimento('');
        setTelefone('');
        setMotivo('');
        setComprovante(null);

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleComprovanteChange = (event) => {
        const selectedFile = event.target.files?.[0] || null;
        setComprovante(selectedFile);
    };

    const submitSolicitacaoReembolso = async (event) => {
        event.preventDefault();

        const normalizedValue = normalizeNumericText(valor);
        const phoneDigits = getDigits(telefone);

        if (!normalizedValue || Number.isNaN(Number(normalizedValue)) || Number(normalizedValue) <= 0) {
            handleAlerta('Informe um valor de reembolso válido.', 'warning');
            return;
        }

        if (phoneDigits.length < 10) {
            handleAlerta('Informe um telefone válido para o cliente.', 'warning');
            return;
        }

        if (!comprovante) {
            handleAlerta('Anexe o comprovante da solicitação de reembolso.', 'warning');
            return;
        }

        setIsSubmit(true);

        const formData = new FormData();
        formData.append('cc', cc);
        formData.append('nf', nf);
        formData.append('valor', normalizedValue);
        formData.append('chave_pix', chavePix);
        formData.append('protocolo_atendimento', protocoloAtendimento);
        formData.append('telefone', telefone);
        formData.append('motivo', motivo);
        formData.append('comprovante', comprovante);

        try {
            const response = await fetchFlask('analiseReembolso', formData);
            setMessage(response.message);
            setStatus(response.status);
            setOpenAlerta(true);

            if (response.status === 'success') {
                clearForm();
            }
        } finally {
            setIsSubmit(false);
        }
    };

    const completedFields = [
        cc,
        nf,
        normalizeNumericText(valor),
        chavePix,
        protocoloAtendimento,
        getDigits(telefone),
        motivo,
        comprovante
    ].filter(Boolean).length;

    const fileName = comprovante?.name || '';

    return (
        <Box className='mainBox refund-request-page'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader>
                Solicitação de Reembolso
            </PageHeader>

            <form
                action=''
                method='POST'
                id='formSolicitacaoReembolso'
                onSubmit={submitSolicitacaoReembolso}
                className='refund-request-layout'
            >
                <Box className='refund-request-workspace'>
                    <Box className='refund-request-intro'>
                        <Box>
                            <span className='refund-request-kicker'>Relacionamento</span>
                            <h2>Análise de reembolso com comprovação e aviso ao cliente</h2>
                        </Box>
                        <p>
                            Registre os dados da solicitação, anexe o comprovante e envie o caso para análise do time responsável.
                        </p>
                    </Box>

                    <Box className='refund-request-panel'>
                        <Box className='refund-request-section-heading'>
                            <ReceiptLongRoundedIcon />
                            <Box>
                                <span>Etapa 1</span>
                                <strong>Identificação do cliente</strong>
                            </Box>
                        </Box>

                        <Box className='refund-request-fields-grid'>
                            <TextField
                                required
                                label='Conta Contrato'
                                value={cc}
                                onChange={(event) => setCc(event.target.value)}
                            />
                            <TextField
                                required
                                label='Nota Fiscal'
                                type='number'
                                inputProps={{ min: 1, step: 1 }}
                                value={nf}
                                onChange={(event) => setNf(event.target.value)}
                            />
                            <TextField
                                required
                                label='Telefone do cliente'
                                value={telefone}
                                onChange={(event) => setTelefone(maskPhone(event.target.value))}
                            />
                            <TextField
                                required
                                label='Protocolo de atendimento'
                                value={protocoloAtendimento}
                                onChange={(event) => setProtocoloAtendimento(event.target.value)}
                            />
                        </Box>
                    </Box>

                    <Box className='refund-request-panel'>
                        <Box className='refund-request-section-heading'>
                            <PaymentsRoundedIcon />
                            <Box>
                                <span>Etapa 2</span>
                                <strong>Dados do reembolso</strong>
                            </Box>
                        </Box>

                        <Box className='refund-request-fields-grid'>
                            <TextField
                                required
                                label='Valor'
                                inputMode='decimal'
                                value={valor}
                                onChange={(event) => setValor(event.target.value)}
                                helperText='Aceita vírgula ou ponto como separador decimal.'
                            />
                            <TextField
                                required
                                label='Chave Pix'
                                value={chavePix}
                                onChange={(event) => setChavePix(event.target.value)}
                            />
                        </Box>

                        <Box className='refund-request-pix-strip'>
                            <PixRoundedIcon />
                            <Box>
                                <span>Destino do crédito</span>
                                <strong>{chavePix || 'Chave Pix ainda não informada'}</strong>
                            </Box>
                        </Box>
                    </Box>

                    <Box className='refund-request-panel'>
                        <Box className='refund-request-section-heading'>
                            <SupportAgentRoundedIcon />
                            <Box>
                                <span>Etapa 3</span>
                                <strong>Contexto e evidência</strong>
                            </Box>
                        </Box>

                        <TextField
                            required
                            multiline
                            minRows={4}
                            label='Motivo da solicitação'
                            value={motivo}
                            onChange={(event) => setMotivo(event.target.value)}
                        />

                        <Box
                            component='label'
                            htmlFor='comprovanteReembolso'
                            className={fileName ? 'refund-request-upload-card has-file' : 'refund-request-upload-card'}
                        >
                            <input
                                ref={fileInputRef}
                                id='comprovanteReembolso'
                                className='refund-request-file-input'
                                type='file'
                                accept='application/pdf,image/png,image/jpeg'
                                onChange={handleComprovanteChange}
                            />
                            <CloudUploadRoundedIcon />
                            <Box>
                                <span>Comprovante</span>
                                <strong>{fileName || 'Selecionar arquivo'}</strong>
                                <p>PDF, PNG ou JPG com a documentação do pedido.</p>
                            </Box>
                            <AttachFileRoundedIcon className='refund-request-attachment-icon' />
                        </Box>
                    </Box>

                    <Box className='refund-request-rail'>
                        <Box>
                            <ContactPhoneRoundedIcon />
                            <span>Contato</span>
                            <strong>{telefone || 'Telefone pendente'}</strong>
                        </Box>
                        <Box>
                            <RequestQuoteRoundedIcon />
                            <span>Reembolso</span>
                            <strong>{getCurrencyPreview(valor)}</strong>
                        </Box>
                    </Box>
                </Box>

                <Box className='refund-request-summary'>
                    <Box className='refund-request-summary-header'>
                        <FactCheckRoundedIcon />
                        <Box>
                            <span>Resumo</span>
                            <strong>Revisão do envio</strong>
                        </Box>
                    </Box>

                    <Box className='refund-request-summary-list'>
                        <Box>
                            <span>Conta contrato</span>
                            <strong className={!cc ? 'is-empty' : undefined}>{cc || 'Não informada'}</strong>
                        </Box>
                        <Box>
                            <span>Nota fiscal</span>
                            <strong className={!nf ? 'is-empty' : undefined}>{nf || 'Não informada'}</strong>
                        </Box>
                        <Box>
                            <span>Valor</span>
                            <strong>{getCurrencyPreview(valor)}</strong>
                        </Box>
                        <Box>
                            <span>Protocolo</span>
                            <strong className={!protocoloAtendimento ? 'is-empty' : undefined}>
                                {protocoloAtendimento || 'Não informado'}
                            </strong>
                        </Box>
                        <Box>
                            <span>Chave Pix</span>
                            <strong className={!chavePix ? 'is-empty' : undefined}>{chavePix || 'Não informada'}</strong>
                        </Box>
                        <Box>
                            <span>Comprovante</span>
                            <strong className={!fileName ? 'is-empty' : undefined}>{fileName || 'Não anexado'}</strong>
                        </Box>
                    </Box>

                    <Box className='refund-request-attention'>
                        <SendRoundedIcon />
                        <p>
                            O time responsável receberá a análise por e-mail e o cliente será avisado que a solicitação está em avaliação.
                        </p>
                    </Box>

                    <Box className='refund-request-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box>
                            <span>Campos críticos</span>
                            <strong>{completedFields}/8 preenchidos</strong>
                        </Box>
                    </Box>

                    <SubmitButton isSubmit={isSubmit} startIcon={<RequestQuoteRoundedIcon />}>
                        Enviar Solicitação
                    </SubmitButton>
                </Box>
            </form>
        </Box>
    );
};

export default SolicitacaoReembolso;
